import { apiClient } from '@/api/client';
import {
  AnalysisListResponse,
  AnalysisDetail,
  AnalysisCreateRequest,
  AnalysisTriggerRequest,
  AnalysisTriggerResponse,
  AnalysisListParams
} from './types';

/**
 * Fetch paginated list of analyses
 */
export async function fetchAnalyses(
  params?: AnalysisListParams
): Promise<AnalysisListResponse> {
  const queryParams = new URLSearchParams();

  if (params?.skip !== undefined) {
    queryParams.append('skip', params.skip.toString());
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }
  if (params?.user_id) {
    queryParams.append('user_id', params.user_id);
  }
  if (params?.status) {
    queryParams.append('status', params.status.toUpperCase());
  }

  const queryString = queryParams.toString();
  const endpoint = `/analyses${queryString ? `?${queryString}` : ''}`;

  return apiClient<AnalysisListResponse>(endpoint, { method: 'GET' });
}

/**
 * Fetch single analysis with findings
 */
export async function fetchAnalysisById(
  analysisId: string
): Promise<AnalysisDetail> {
  return apiClient<AnalysisDetail>(`/analyses/${analysisId}`, {
    method: 'GET'
  });
}

/**
 * Create new analysis (doesn't start execution)
 */
export async function createAnalysis(
  data: AnalysisCreateRequest
): Promise<AnalysisDetail> {
  return apiClient<AnalysisDetail>('/analyses/', {
    method: 'POST',
    data
  });
}

/**
 * Trigger analysis execution
 */
export async function triggerAnalysis(
  analysisId: string,
  config?: AnalysisTriggerRequest
): Promise<AnalysisTriggerResponse> {
  return apiClient<AnalysisTriggerResponse>(`/analyses/${analysisId}/trigger`, {
    method: 'POST',
    data: config
  });
}

/**
 * Delete analysis (soft delete)
 */
export async function deleteAnalysis(analysisId: string): Promise<void> {
  return apiClient<void>(`/analyses/${analysisId}`, {
    method: 'DELETE'
  });
}

/**
 * Check availability of analysis name
 */
export async function checkAnalysisNameAvailability(
  name: string
): Promise<boolean> {
  const encodedName = encodeURIComponent(name);
  return apiClient<boolean>(`/analyses/analysis-name-available/${encodedName}`);
}

/**
 * Retrieve server configuration/feature flags for analyses
 */
export async function fetchAnalysisConfig(): Promise<{
  analysis_enabled: boolean;
}> {
  return apiClient<{ analysis_enabled: boolean }>(`/analyses/config`, {
    method: 'GET'
  });
}
